import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchDevice, fetchDevices, updateDevice, blockDevice, banIpDevice } from "../../actions/mine_craft/devicesActions";
import { fetchAccounts } from "../../actions/mine_craft/accountsActions";

export interface DeviceInterface {
  url: string;

  id: number;
  coins: number;
  advertise: string | null;
  vendor: string | null;

  notify_token: number | null;
  timezone: number;
  language: string;
  country_code: string | null;
  device_type: string | null;
  os_version: string | null;

  native_ads: boolean;
  banner: boolean;
  app_open: boolean;
  interstitial: boolean;
  interstitial_long: boolean;

  notify_youtube_video: boolean;
  notify_app_update: boolean;
  notify_daily_rewards: boolean;
  step_youtube_video: boolean;
  step_app_update: boolean;

  banned: boolean;
  banned_until: string;

  ip: string | null;
  last_ip: string | null;
  create_ip: string | null;
  launch_count: number;
  launch_at: string | null;

  // v2 auth scheme that enrolled the device's newest attestation key.
  // Only present in the #show payload (show_details); index rows omit it.
  attestation_kind?: 'app_attest' | 'device_key' | null;

  bundle?: {
    id: EntityId;
    name: string;
  };

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  banId: EntityId[] | null;
  banError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  banId: null,
  banError: null,
};

const adaptor = createEntityAdapter<DeviceInterface>();

const slice = createSlice({
  name: 'devices',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setBanId: (state, action) => {
      state.banId    = action.payload;
      state.banError = initialState.banError;
    },
    setBanError: (state, action) => {
      state.banError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchDevices.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.devices;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchDevice.fulfilled, (state, action) => {
        const device = Object.values(action.payload.device)[0];
        state.entities[device.id] = device;
      })

      // #update
      .addCase(updateDevice.fulfilled, (state, action) => {
        const device = Object.values(action.payload.device)[0];
        state.entities[device.id] = device;
      })
      .addCase(updateDevice.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // accounts
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.devices };
      })

      // #ban
      .addCase(blockDevice.fulfilled, (state, action) => {
        const device = Object.values(action.payload.device)[0];
        state.entities[device.id] = device;
      })
      .addCase(blockDevice.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.banError = error || action.error.message || 'Unknown Error';
      })

      // #ban_ip
      .addCase(banIpDevice.fulfilled, (state, action) => {
        const device = Object.values(action.payload.device)[0];
        state.entities[device.id] = device;
      })
      .addCase(banIpDevice.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.banError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  setBanId,
  setBanError
} = slice.actions;

export const {
  selectById: selectDeviceById,
  selectIds: selectDeviceIds,
  selectEntities: selectDeviceEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.devices);
